import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  Select,
  TextField,
  Button,
  BlockStack,
  InlineStack,
  Banner,
  Text,
  Divider,
  Box,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { assetsApi, productsApi } from '@/services';
import { useAppBridge } from '@shopify/app-bridge-react';

interface BatchRow {
  id: string;
  serialNumber: string;
  gpsId: string;
  lotNumber: string;
  purchaseCost: string;
  location: string;
  notes: string;
  serialError: string;
}

function makeBlankRow(uid: string): BatchRow {
  return {
    id: uid,
    serialNumber: '',
    gpsId: '',
    lotNumber: '',
    purchaseCost: '',
    location: '',
    notes: '',
    serialError: '',
  };
}

let rowCounter = 0;
function nextId() {
  return `row-${++rowCounter}`;
}

export default function AddProductInventory() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const shopify = useAppBridge();

  const [selectedProductId, setSelectedProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [productSku, setProductSku] = useState('');

  const [rows, setRows] = useState<BatchRow[]>([makeBlankRow(nextId())]);
  const [submitting, setSubmitting] = useState(false);
  const [bannerErrors, setBannerErrors] = useState<{ serial: string; message: string }[]>([]);
  const [bannerTone, setBannerTone] = useState<'warning' | 'critical'>('critical');

  // ── Product data ──────────────────────────────────────────────────────────
  const { data: productsRaw } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => productsApi.list(),
  });

  const rentalProducts = useMemo(
    () =>
      (productsRaw ?? []).filter(
        (p) => p.status === 'ACTIVE' && p.rentalConfig.isRentalProduct,
      ),
    [productsRaw],
  );

  const productOptions = useMemo(
    () => [
      { label: '— Select a product —', value: '' },
      ...rentalProducts.map((p) => ({ label: p.title, value: p.shopifyProductId })),
    ],
    [rentalProducts],
  );

  const handleProductSelect = useCallback(
    (productId: string) => {
      setSelectedProductId(productId);
      if (!productId) {
        setProductName('');
        setProductSku('');
        return;
      }
      const product = rentalProducts.find((p) => p.shopifyProductId === productId);
      if (product) {
        setProductName(product.title);
        setProductSku(product.variants[0]?.sku ?? '');
      }
    },
    [rentalProducts],
  );

  // ── Row management ────────────────────────────────────────────────────────
  const updateRow = useCallback((id: string, field: keyof BatchRow, value: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, [field]: value, ...(field === 'serialNumber' ? { serialError: '' } : {}) }
          : r,
      ),
    );
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, makeBlankRow(nextId())]);
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = useCallback((): boolean => {
    let valid = true;
    const seen = new Set<string>();

    setRows((prev) =>
      prev.map((r) => {
        let serialError = '';
        const serial = r.serialNumber.trim();

        if (!serial) {
          serialError = 'Serial number is required';
          valid = false;
        } else if (seen.has(serial.toLowerCase())) {
          serialError = 'Duplicate serial number in this batch';
          valid = false;
        } else {
          seen.add(serial.toLowerCase());
        }

        return { ...r, serialError };
      }),
    );

    return valid;
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!selectedProductId) return;
    if (!validate()) return;

    setSubmitting(true);
    setBannerErrors([]);

    const results = await Promise.allSettled(
      rows.map((r) =>
        assetsApi.create({
          serialNumber: r.serialNumber.trim(),
          gpsId: r.gpsId.trim() || undefined,
          productSku,
          productName,
          lotNumber: r.lotNumber.trim() || undefined,
          purchaseCost: r.purchaseCost.trim()
            ? parseFloat(r.purchaseCost.trim())
            : undefined,
          notes: r.notes.trim() || undefined,
        }),
      ),
    );

    setSubmitting(false);

    const failures: { serial: string; message: string }[] = [];
    const successIds: string[] = [];

    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') {
        successIds.push(rows[idx].id);
      } else {
        const err = result.reason as { message?: string } | Error;
        failures.push({
          serial: rows[idx].serialNumber.trim(),
          message:
            (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            (err as Error).message ??
            'Unknown error',
        });
      }
    });

    const successCount = successIds.length;

    if (failures.length === 0) {
      // Full success
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      shopify.toast.show(`Added ${successCount} unit${successCount !== 1 ? 's' : ''} to inventory`);
      navigate('/assets');
    } else if (successCount > 0) {
      // Partial success — remove the rows that succeeded, keep failures
      setRows((prev) => prev.filter((r) => !successIds.includes(r.id)));
      setBannerTone('warning');
      setBannerErrors(failures);
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
    } else {
      // Total failure
      setBannerTone('critical');
      setBannerErrors(failures);
    }
  }, [selectedProductId, rows, productSku, productName, validate, queryClient, shopify, navigate]);

  const canSubmit = !!selectedProductId && !submitting;

  return (
    <Page
      title="Add Product Inventory"
      backAction={{ content: 'Assets', url: '/assets' }}
    >
      <BlockStack gap="500">
        {/* ── Error / partial-success banner ── */}
        {bannerErrors.length > 0 && (
          <Banner
            tone={bannerTone}
            title={
              bannerTone === 'warning'
                ? 'Some units could not be added'
                : 'Failed to add units to inventory'
            }
            onDismiss={() => setBannerErrors([])}
          >
            <BlockStack gap="100">
              {bannerErrors.map(({ serial, message }) => (
                <Text key={serial} as="p" variant="bodyMd">
                  <strong>{serial}</strong>: {message}
                </Text>
              ))}
            </BlockStack>
          </Banner>
        )}

        {/* ── Product selection ── */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Select Product
            </Text>
            <Select
              label="Product"
              options={productOptions}
              value={selectedProductId}
              onChange={handleProductSelect}
              requiredIndicator
            />
            {selectedProductId && (
              <InlineStack gap="400">
                <Text as="p" variant="bodyMd">
                  <strong>Name:</strong> {productName}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>SKU:</strong> {productSku}
                </Text>
              </InlineStack>
            )}
          </BlockStack>
        </Card>

        {/* ── Batch rows ── */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Units to Add
            </Text>

            {rows.map((row, index) => (
              <BlockStack key={row.id} gap="300">
                {index > 0 && <Divider />}
                <InlineStack align="space-between" blockAlign="start">
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Unit {index + 1}
                  </Text>
                  <Button
                    icon={DeleteIcon}
                    variant="plain"
                    tone="critical"
                    accessibilityLabel={`Remove unit ${index + 1}`}
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length === 1}
                  />
                </InlineStack>

                <InlineStack gap="300" wrap>
                  <Box minWidth="160px" width="calc(40% - 12px)">
                    <TextField
                      label="Serial Number"
                      value={row.serialNumber}
                      onChange={(v) => updateRow(row.id, 'serialNumber', v)}
                      autoComplete="off"
                      requiredIndicator
                      error={row.serialError || undefined}
                      autoFocus={index === 0}
                    />
                  </Box>
                  <Box minWidth="160px" width="calc(35% - 12px)">
                    <TextField
                      label="GPS ID (optional)"
                      value={row.gpsId}
                      onChange={(v) => updateRow(row.id, 'gpsId', v)}
                      autoComplete="off"
                      placeholder="Tracker ID"
                    />
                  </Box>
                  <Box minWidth="120px" width="calc(25% - 12px)">
                    <TextField
                      label="Lot Number"
                      value={row.lotNumber}
                      onChange={(v) => updateRow(row.id, 'lotNumber', v)}
                      autoComplete="off"
                    />
                  </Box>
                </InlineStack>

                <InlineStack gap="300" wrap>
                  <Box minWidth="200px" width="calc(50% - 12px)">
                    <TextField
                      label="Purchase Cost"
                      value={row.purchaseCost}
                      onChange={(v) => updateRow(row.id, 'purchaseCost', v)}
                      autoComplete="off"
                      type="number"
                      prefix="$"
                    />
                  </Box>
                  <Box minWidth="200px" width="calc(50% - 12px)">
                    <TextField
                      label="Location"
                      value={row.location}
                      onChange={(v) => updateRow(row.id, 'location', v)}
                      autoComplete="off"
                      placeholder="Warehouse"
                    />
                  </Box>
                </InlineStack>

                <TextField
                  label="Notes"
                  value={row.notes}
                  onChange={(v) => updateRow(row.id, 'notes', v)}
                  autoComplete="off"
                  multiline={2}
                />
              </BlockStack>
            ))}

            <Button onClick={addRow} variant="plain">
              + Add another unit
            </Button>
          </BlockStack>
        </Card>

        {/* ── Submit ── */}
        <InlineStack align="end">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            {`Add ${rows.length} Unit${rows.length !== 1 ? 's' : ''} to Inventory`}
          </Button>
        </InlineStack>
      </BlockStack>
    </Page>
  );
}
